import { useState } from "react"
import "./styles.scss"
import { engagementStore } from "src/modules/engagement/engagementStore"
import { useNavigate, useParams } from "react-router-dom"
import { Paths } from "src/paths"
import { useItemRoute } from "src/modules/engagement/workflow/useItemRoute"
import { useEngagementBudget } from "./hooks/useEngagementBudget"
import { BudgetHeader } from "./components/BudgetHeader"
import { EngagementLaunchesTable } from "./components/EngagementLaunchesTable"
import { CampaignSegmentBudget } from "./segments/CampaignSegmentBudget"
import { CustomizeBudget } from "./segments/CustomizeBudget"
import type { SpendPattern } from "./segments/segmentEngine"
import type { LaunchBudgetSavePayload } from "@services/engagements/budget/commands/cmdSaveLaunchBudget"
import { campaignNetworks, windowNetworks } from "./segments/toSavePayload"
import {
  toAllocation,
  toDefaultMix,
  toFlight,
  toScheduledRanges,
  toSegmentNetworks,
  toSegments,
} from "./segments/fromBudgetTree"

/*
    Budget 2.0 — the segmented Budget screen, trialled beside the one it may replace
    (MPN-1792).

    Deliberately its own component rather than a mode of `Budget`. The point of running the
    two side by side is that the original keeps behaving exactly as it does today, and a
    shared component with a `variant` prop cannot promise that — every change here would be
    a change there, one conditional at a time.

    What they DO share is the data: the same `useEngagementBudget` hook, so both tabs read
    one budget tree and one header from the same two endpoints. Divergence can only come
    from presentation, never from a second source of figures.
*/
export const Budget2 = () => {
  const engagementId = engagementStore.selectedEngagement.id

  // Same URL mechanism as Budget, so a campaign can be linked and walked with the back
  // button on either tab.
  const [openLaunchId, setOpenLaunchId] = useItemRoute()
  const [editingList, setEditingList] = useState(false)
  //: Raised by the table when the campaigns add to more than 100%.
  const [overAllocated, setOverAllocated] = useState(false)

  /* Publish and Campaigns are sibling workflow items, so each hop is a tab change on the
     same engagement. Read the engagement from the URL rather than the store: a host that
     renders this outside the engagement route has no tab to swap, and sending it to a
     half-built path is worse than leaving the link inert.

     The slug is the item key with underscores hyphenated (workflowItems.ts), which for
     "campaign" and "publish" is the key unchanged. */
  const navigate = useNavigate()
  const { engagementId: routeEngagementId } = useParams<{ engagementId: string }>()
  const goToItem = routeEngagementId
    ? (slug: string, itemId?: number) =>
        navigate(
          `${import.meta.env.BASE_URL}${Paths.ENGAGEMENT}/${routeEngagementId}/${slug}` +
            (itemId == null ? "" : `/${itemId}`),
        )
    : undefined
  const goToPublish = goToItem && (() => goToItem("publish"))

  const { header, tree, loading, error, setLaunchPolicy, saveLaunchBudget, migrateLaunchBudget } =
    useEngagementBudget(engagementId)
  const [migrating, setMigrating] = useState(false)
  const [migrateError, setMigrateError] = useState<string | null>(null)

  if (loading) return <Spinner />
  if (error) return <ErrorState message={error} />
  if (!header || !tree) return <ErrorState message="No budget data" />

  const openLaunch = tree.launches.find((l) => l.launch_id === openLaunchId)

  if (openLaunchId != null && !openLaunch) {
    return (
      <div className="budget-pane">
        <button type="button" className="budget-detail__back" onClick={() => setOpenLaunchId(null)}>
          <i className="bi bi-chevron-left me-1" />
          Campaigns
        </button>
        <div className="budget-empty">
          <div className="budget-empty__title">Campaign not found</div>
          <div className="budget-empty__body">
            This campaign is no longer part of the engagement&apos;s budget.
          </div>
        </div>
      </div>
    )
  }

  if (openLaunch) {
    const cycleDays = header.days_until_replenish
    const cycleLabel = cycleDays == null ? "—" : `${cycleDays} day${cycleDays === 1 ? "" : "s"}`
    // The campaign's own dates where it has them; the engagement's window only as a last
    // resort for a campaign that has never been scheduled.
    const flight = toFlight(openLaunch, {
      start: new Date().toISOString().slice(0, 10),
      end: header.funding_end ?? new Date().toISOString().slice(0, 10),
    })

    // Mapped once: the table renders it and the save payload reads each network's cap and
    // bid back off it, and the two must be the same list.
    const segmentNetworks = toSegmentNetworks(openLaunch.networks)
    const today = new Date().toISOString().slice(0, 10)

    /*
        A legacy campaign is gated behind Customize Budget (MPN-1803). Opening the editable
        screen for one would migrate it as a side effect of the first Save, and migration is
        not reversible -- the campaign then reads differently on the old Budget screen and
        publishes from different policies.
    */
    if (openLaunch.spend_pattern === "LEGACY") {
      return (
        <div className="budget-pane budget-pane--segmented">
          <CustomizeBudget
            campaignName={openLaunch.launch_name}
            amount={openLaunch.resolved_amount}
            flight={flight}
            today={today}
            busy={migrating}
            error={migrateError}
            onBack={() => setOpenLaunchId(null)}
            onMigrate={async () => {
              setMigrating(true)
              setMigrateError(null)
              try {
                await migrateLaunchBudget(openLaunch.launch_id)
              } catch (err) {
                setMigrateError(err instanceof Error ? err.message : String(err))
              } finally {
                setMigrating(false)
              }
            }}
          />
        </div>
      )
    }

    return (
      <div className="budget-pane budget-pane--segmented">
        <CampaignSegmentBudget
          onBack={() => setOpenLaunchId(null)}
          engagement={{
            balance: header.balance,
            daysRemaining: String(header.active_days_remaining),
            nextBillingCycle: cycleLabel,
            engagementSpend: header.total_debits ?? 0,
          }}
          campaignName={openLaunch.launch_name}
          allocation={toAllocation(openLaunch)}
          savedMargin={openLaunch.margin}
          // The pattern the campaign actually has, not a hardcoded guess — otherwise a
          // campaign saved as Monthly reopens claiming to be Even.
          savedPattern={toSpendPattern(openLaunch.spend_pattern)}
          flight={flight}
          windowStart={flight.start}
          windowEnd={flight.end}
          fundedDays={header.spend_window_days}
          daysRemainingLabel={String(header.active_days_remaining)}
          nextBillingLabel={cycleLabel}
          nextReplenishDate={header.next_replenish_date}
          billingMethod={header.billing_method || "—"}
          scheduledRanges={toScheduledRanges(openLaunch)}
          goLiveDate={openLaunch.go_live_date}
          onGoToPublish={goToPublish}
          // The campaign's dates are read-only here and editable on Campaigns, which is
          // what the padlocks beside them claim. The link has to actually land there or
          // the padlock is pointing at nothing.
          onEditCampaign={goToItem && (() => goToItem("campaign", openLaunch.launch_id))}
          networks={segmentNetworks}
          defaultMix={toDefaultMix(openLaunch)}
          initialSegments={toSegments(openLaunch, flight)}
          onCommit={(_entries, segments, pattern, marginPct, capEdits) =>
            saveLaunchBudget(openLaunch.launch_id, {
              spend_pattern: pattern.toUpperCase() as LaunchBudgetSavePayload["spend_pattern"],
              // A window the operator drew has no id yet; one that came from the server
              // keeps its own, so the command can tell an edit from an addition. Anything
              // missing from this list is a window they deleted.
              segments: segments.map((s) => ({
                id: numericId(s.id),
                // The engine does not name windows; the server labels them from their dates.
                name: "",
                start_time: s.start,
                end_time: s.end,
                amount: s.amount,
                // Always sent, never omitted: this screen owns every window's split, so an
                // empty list really does mean "no overrides" rather than "no opinion".
                networks: windowNetworks(s, segmentNetworks, capEdits),
              })),
              margin: marginPct,
              // Daily Cap belongs to the network across the campaign, not to one window,
              // so it rides here. Only networks whose cap actually changed appear.
              networks: campaignNetworks(segmentNetworks, capEdits, (key) => {
                const node = openLaunch.networks.find(
                  (n) => n.network.toLowerCase() === key,
                )
                return node?.percent
              }),
            })
          }
          today={today}
          // LEGACY means the campaign has never been through Customize Budget, and its
          // first save is the one that establishes its windows — which MPN-1807 says does
          // not get a history entry, because nothing has changed from Ops' point of view.
          configured={openLaunch.spend_pattern !== "LEGACY"}
        />
      </div>
    )
  }

  // The campaign list is unchanged from Budget: same header, same table, same percent
  // writes. Only the drill-in differs, which is the whole point of the comparison.
  return (
    <div className="budget-pane">
      <div className="budget-strip">
        <BudgetHeader header={header} />
      </div>

      {tree.launches.length === 0 ? (
        <div className="budget-empty">
          <div className="budget-empty__title">No campaigns yet</div>
          <div className="budget-empty__body">
            The engagement&apos;s remaining flight funding is split across its campaigns. Create a
            campaign first, then come back here to allocate it.
          </div>
        </div>
      ) : (
        <div className="budget-card">
          <div className="budget-card__head">
            <span className="budget-card__title">Campaigns</span>
            <span className="budget-card__count">{tree.launches.length}</span>
            {/* Over-allocation blocks the way out of Edit rather than the individual
                edits: an operator moving one campaign's share from 40 to 60 has to pass
                through a total over 100 to get there, and refusing each keystroke would
                make the screen unusable (MPN-1804). */}
            {overAllocated && (
              <span className="budget-blocked-note">
                Total is over 100% — reduce it before finishing.
              </span>
            )}
            <button
              type="button"
              className="btn btn-sm btn-outline-primary ms-auto"
              style={{ fontSize: "0.72rem" }}
              disabled={editingList && overAllocated}
              title={
                editingList && overAllocated
                  ? "The campaigns add up to more than the engagement has."
                  : undefined
              }
              onClick={() => setEditingList((e) => !e)}
            >
              <i className={`bi ${editingList ? "bi-check-lg" : "bi-pencil"} me-1`} />
              {editingList ? "Done" : "Edit"}
            </button>
          </div>
          <EngagementLaunchesTable
            launches={tree.launches}
            funding={tree.remaining_flight_funding}
            editing={editingList}
            onSelectLaunch={setOpenLaunchId}
            onOverAllocatedChange={setOverAllocated}
            onSetPercent={(launchId, percent) => setLaunchPolicy({ launch_id: launchId, percent })}
          />
        </div>
      )}
    </div>
  )
}

function Spinner() {
  return (
    <div className="d-flex align-items-center justify-content-center py-5">
      <div className="spinner-border text-secondary" role="status">
        <span className="visually-hidden">Loading...</span>
      </div>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="budget-pane">
      <div className="budget-empty">
        <div className="budget-empty__title">Budget error</div>
        <div className="budget-empty__body">{message}</div>
      </div>
    </div>
  )
}

/*
    Segment ids are strings on this screen because the engine mints its own for windows that
    do not exist server-side yet ("s4"). A numeric one came from the server and names the row
    to update; anything else is new.
*/
function numericId(id: string): number | undefined {
  return /^\d+$/.test(id) ? Number(id) : undefined
}

/*
    The server names patterns in upper case and the screen in lower. LEGACY has no screen
    equivalent — a campaign that has never been customised opens on Even, which is what
    Customize Budget will seed it with (MPN-1803).
*/
function toSpendPattern(stored: string): SpendPattern {
  const lowered = stored.toLowerCase()
  return lowered === "monthly" || lowered === "custom" ? lowered : "even"
}
